// @ts-nocheck
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import notify from '../utils/toast';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../context/I18nContext';
import ActionButtons from '../components/ActionButtons';
import AppModal from '../components/AppModal';

const STATUS_BADGE = {
    pending: 'secondary',
    confirmed: 'success',
    cancelled: 'danger',
    handed_over: 'primary',
    transferred: 'info',
};

export default function Bookings() {
    const [bookings, setBookings] = useState([]);
    const [projects, setProjects] = useState([]);
    const [units, setUnits] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [form, setForm] = useState({
        project_id: '', unit_id: '', customer_id: '', booking_date: new Date().toISOString().slice(0, 10),
        booking_money: '', sale_price: '', discount: '0', commission_percent: '0', notes: '',
    });
    const { hasRole, can } = useAuth();
    const { t, td } = useI18n();

    const closeBookingModal = () => {
        setShowModal(false);
        setForm({
            project_id: '', unit_id: '', customer_id: '', booking_date: new Date().toISOString().slice(0, 10),
            booking_money: '', sale_price: '', discount: '0', commission_percent: '0', notes: '',
        });
    };

    const load = () => {
        setLoading(true);
        api.get('/bookings').then((res) => setBookings(res.data.data)).finally(() => setLoading(false));
    };

    useEffect(() => {
        load();
        if (hasRole('admin', 'sales')) {
            api.get('/projects').then((r) => setProjects(r.data.data));
            api.get('/auth/users', { params: { role: 'customer' } }).then((r) => setCustomers(r.data.data)).catch(() => {});
        }
    }, []);

    useEffect(() => {
        if (form.project_id) {
            api.get('/units', { params: { project_id: form.project_id, status: 'available' } })
                .then((r) => setUnits(r.data.data));
        } else {
            setUnits([]);
        }
    }, [form.project_id]);

    const onUnitChange = (unitId) => {
        const u = units.find((x) => String(x.id) === String(unitId));
        setForm((f) => ({
            ...f,
            unit_id: unitId,
            sale_price: u ? u.sale_price : f.sale_price,
        }));
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        try {
            const final = Number(form.sale_price) - Number(form.discount || 0);
            // Simple default installments: booking + 4 equal
            const remaining = final - Number(form.booking_money || 0);
            const perInst = Math.round(remaining / 4);
            const installments = [
                { title: 'Booking Money', due_date: form.booking_date, amount: Number(form.booking_money || 0) },
                { title: '1st Installment', due_date: addMonths(form.booking_date, 3), amount: perInst },
                { title: '2nd Installment', due_date: addMonths(form.booking_date, 6), amount: perInst },
                { title: '3rd Installment', due_date: addMonths(form.booking_date, 9), amount: perInst },
                { title: 'Final / Handover', due_date: addMonths(form.booking_date, 12), amount: remaining - perInst * 3 },
            ];
            await api.post('/bookings', { ...form, installments });
            closeBookingModal();
            load();
        } catch (err) {
            notify.apiError(err);
        }
    };

    function addMonths(dateStr, months) {
        const d = new Date(dateStr);
        d.setMonth(d.getMonth() + months);
        return d.toISOString().slice(0, 10);
    }

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h4 className="mb-1">Bookings & Sales</h4>
                    <p className="text-muted mb-0">Customer bookings and unit allocation</p>
                </div>
                {can('bookings.create') && (
                    <button className="btn btn-primary" onClick={() => setShowModal(true)}>
                        <i className="bi bi-journal-plus me-1"></i> New Booking
                    </button>
                )}
            </div>

            <div className="card table-card">
                <div className="table-responsive">
                    <table className="table table-hover mb-0">
                        <thead className="table-light">
                            <tr>
                                <th>Booking No</th>
                                <th>Customer</th>
                                <th>Project / Unit</th>
                                <th>Final Price</th>
                                <th>Date</th>
                                <th>Status</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={7} className="text-center py-4"><div className="spinner-border spinner-border-sm text-success" /></td></tr>
                            ) : bookings.length === 0 ? (
                                <tr><td colSpan={7} className="text-center text-muted py-4">No bookings yet</td></tr>
                            ) : bookings.map((b) => (
                                <tr key={b.id}>
                                    <td className="fw-semibold">{b.booking_no}</td>
                                    <td>
                                        <div>{b.customer?.name}</div>
                                        <small className="text-muted">{b.customer?.phone}</small>
                                    </td>
                                    <td>
                                        <div>{b.project?.name}</div>
                                        <small className="text-muted">Unit {b.unit?.unit_number}</small>
                                    </td>
                                    <td>৳ {Number(b.final_price).toLocaleString()}</td>
                                    <td>{b.booking_date}</td>
                                    <td><span className={`badge bg-${STATUS_BADGE[b.status] || 'secondary'} badge-status`}>{td('status', b.status)}</span></td>
                                    <td>
                                        <ActionButtons
                                            viewTo={`/bookings/${b.id}`}
                                            canView={can('bookings.view')}
                                            canEdit={can('bookings.edit')}
                                            canDelete={can('bookings.delete')}
                                            onEdit={() => window.location.assign(`/bookings/${b.id}`)}
                                            onDelete={async () => {
                                                if (!window.confirm('Cancel/delete this booking?')) return;
                                                try {
                                                    await api.post(`/bookings/${b.id}/cancel`);
                                                    load();
                                                } catch (e) {
                                                    notify.apiError(e);
                                                }
                                            }}
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {showModal && (
                <div className="modal show d-block app-modal" onClick={closeBookingModal} style={{ background: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-lg modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
                        <form className="modal-content" onSubmit={handleCreate}>
                            <div className="modal-header">
                                <h5 className="modal-title">Create Booking</h5>
                                <button type="button" className="btn-close" onClick={closeBookingModal} />
                            </div>
                            <div className="modal-body">
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <label className="form-label">Project *</label>
                                        <select className="form-select" required value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value, unit_id: '' })}>
                                            <option value="">Select</option>
                                            {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                                        </select>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Unit (Available) *</label>
                                        <select className="form-select" required value={form.unit_id} onChange={(e) => onUnitChange(e.target.value)}>
                                            <option value="">Select</option>
                                            {units.map((u) => (
                                                <option key={u.id} value={u.id}>
                                                    {u.unit_number} ({u.unit_type?.replace('_',' ')}) — ৳ {Number(u.sale_price).toLocaleString()}
                                                    {u.bedrooms ? ` · ${u.bedrooms} bed` : ''}
                                                    {u.land_share_decimal ? ` · share ${u.land_share_decimal}` : ''}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Customer *</label>
                                        <select className="form-select" required value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value })}>
                                            <option value="">Select</option>
                                            {customers.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.phone})</option>)}
                                        </select>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Booking Date *</label>
                                        <input type="date" className="form-control" required value={form.booking_date} onChange={(e) => setForm({ ...form, booking_date: e.target.value })} />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label">Sale Price *</label>
                                        <input type="number" className="form-control" required value={form.sale_price} onChange={(e) => setForm({ ...form, sale_price: e.target.value })} />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label">Discount</label>
                                        <input type="number" className="form-control" value={form.discount} onChange={(e) => setForm({ ...form, discount: e.target.value })} />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label">Booking Money</label>
                                        <input type="number" className="form-control" value={form.booking_money} onChange={(e) => setForm({ ...form, booking_money: e.target.value })} />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label">Commission %</label>
                                        <input type="number" step="0.01" className="form-control" value={form.commission_percent} onChange={(e) => setForm({ ...form, commission_percent: e.target.value })} />
                                    </div>
                                    <div className="col-12">
                                        <label className="form-label">Notes</label>
                                        <textarea className="form-control" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={closeBookingModal}>Cancel</button>
                                <button type="submit" className="btn btn-primary">Create Booking</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
